import { Head, NoData, SkeletonLoader, Total } from '@/components';
import Slider from '@/components/Slider';
import { useMuiMediaQuery } from '@/hooks/useMediaQuery';
import { roundNumber } from '@/utils/maths';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import json2mq from 'json2mq';
import { useRef, useState } from 'react';
import { useActivities } from '../hooks/useActivities';
import { useGetInitialValue } from '../hooks/useGetInitialValue';
import styles from '../style.module.css';
import {
  filterByKeys,
  getActivityTotalDistancePerWeek,
  getRunningActivities,
  parseAllActivities,
} from '../utils/activities';
import { black, green, meanLineStyle, titleStyle } from '../utils/common';
import {
  formatWeekNumber,
  lastWeekNumber,
  maxWeeksToDisplay,
} from '../utils/date';

const valuetext = (value: number) => {
  return `${value}km`;
};

const RunningChart = () => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [threshold, setThreshold] = useState(60);
  const { activities, isLoading } = useActivities();

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  const allActivities = activities.map(parseAllActivities);

  const runningActivities = getRunningActivities(allActivities);
  const { initialValue } = useGetInitialValue();
  const isMobile = useMuiMediaQuery(json2mq({ maxWidth: 600 }));

  const limit = lastWeekNumber - maxWeeksToDisplay;

  let runningStats = runningActivities.reduce(getActivityTotalDistancePerWeek, {
    ...initialValue,
  });
  if (isMobile && lastWeekNumber > maxWeeksToDisplay) {
    runningStats = filterByKeys(runningStats, limit);
  }

  const runningStatsValues = Object.values(runningStats);

  const categories = Object.keys(runningStats).map(
    (yearWeek) => formatWeekNumber(Number(yearWeek.slice(4))) // 2021-01 => 01
  );

  const total = runningStatsValues.reduce((acc, distance) => acc + distance, 0);

  const nbWeeks = Object.values(runningStats).filter((e) => e).length;
  const mean = roundNumber(total / nbWeeks);
  const max = Math.max(...runningStatsValues) + 30;

  const colors = runningStatsValues.map((distance) =>
    distance >= threshold ? green : '#f7a35c'
  );

  const handleChange = (_event: Event, value: number | number[]) => {
    const threshold = typeof value === 'number' ? value : value[0];
    setThreshold(threshold);
  };

  const nbWeeksUpperThreshold = runningStatsValues.filter(
    (distance) => distance >= threshold
  ).length;

  const nbWeeksUpperThresholdText =
    nbWeeksUpperThreshold < 10
      ? `0${nbWeeksUpperThreshold}`
      : nbWeeksUpperThreshold;

  const options = {
    chart: {
      type: 'column',
      zoomType: 'x',
    },
    title: {
      text: 'Volume semaine cap en km',
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories,
      title: {
        text: 'week',
      },
    },
    yAxis: {
      title: {
        text: 'distance (km)',
      },
    },
    plotOptions: {
      column: {
        colorByPoint: true,
      },
    },
    tooltip: {
      valueSuffix: ` km`,
      valueDecimals: 2,
    },
    series: [
      {
        name: 'running',
        data: runningStatsValues,
        colors,
        dataLabels: {
          enabled: true,
          format: '{point.y:.1f}',
        },
      },
      {
        name: 'mean',
        color: black,
        data: [
          [0, mean],
          [nbWeeks, mean],
        ],
        ...meanLineStyle,
      },
    ],
  };

  return (
    <div id="running">
      <Head title="Strava Dashboard - running" />
      <div className={styles.total}>
        <Total total={total} unit="km" />
        <div>
          <b>Moyenne:</b>
          {` ${mean} km / semaine`}
        </div>
        <Slider
          max={Math.ceil(max)}
          value={threshold}
          onChange={handleChange}
          getAriaValueText={valuetext}
          width="200px"
        />
        {`${threshold} km (${nbWeeksUpperThresholdText} semaines)`}
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
    </div>
  );
};

export default RunningChart;
